import { type StoreActionApi } from 'react-sweet-state';

import { type ChatStorePrivateState } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { fetchConversationChannelsData } from './fetchConversationChannelsData';
import type { ChatStoreAction } from './types';

const INITIAL_LOAD_THROTTLE_MS = 500;

type InitialLoad = {
	siteId: string | undefined;
	request?: Promise<void>;
	settledAt?: number;
};

// Keyed by the store's `getState`. Every container sharing the global store receives the same getState.
const initialLoads = new WeakMap<StoreActionApi<ChatStorePrivateState>['getState'], InitialLoad>();

/**
 * Shares an in-flight initial conversations fetch across every container init for the same site,
 * and skips inits within a short window after it settles.
 */
export const fetchInitialConversationChannelsOnce =
	(): ChatStoreAction<Promise<void>> =>
	async ({ getState, dispatch }, { siteId }) => {
		const initialLoad = initialLoads.get(getState);
		if (initialLoad?.siteId === siteId) {
			// Already a fetch in progress
			if (initialLoad.request) {
				return initialLoad.request;
			}
			// Already had a fetch complete within the throttle window
			if (
				initialLoad.settledAt !== undefined &&
				Date.now() - initialLoad.settledAt < INITIAL_LOAD_THROTTLE_MS
			) {
				return;
			}
		}

		const request = dispatch(fetchConversationChannelsData({ isInitialLoad: true }));
		initialLoads.set(getState, { siteId, request });

		try {
			await request;
		} finally {
			if (initialLoads.get(getState)?.request === request) {
				initialLoads.set(getState, { siteId, settledAt: Date.now() });
			}
		}
	};
